import React, { useState, useEffect } from 'react';
import { Drawer, Typography, Avatar, Box, List } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { MedicalServicesOutlined } from '@mui/icons-material';
import { deepPurple } from "@mui/material/colors";
import { ROUTES } from '../../constants/routes';
import { authService } from '../../services/authService';
import { ConfirmDialog } from './ConfirmDialog';
import { useMenuItems } from './hooks/useMenuItems';
import { MenuItemComponent } from './MenuItemComponent';
import { MenuItem } from './menuConfig';

// Константы времени анимации аккордеона (должны совпадать с MenuItemComponent)
const ACCORDION_ANIMATION_DURATION = {
    enter: 500, // Время открытия в миллисекундах
    exit: 500,  // Время закрытия в миллисекундах
};

export const Sidebar = () => {
    const [accordionOpen, setAccordionOpen] = useState<{ [key: string]: boolean }>({});
    const [openDialog, setOpenDialog] = useState(false);
    const [isManualToggle, setIsManualToggle] = useState(false);
    const [closingIndex, setClosingIndex] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const menuItemsState = useMenuItems();

    const handleLogout = () => {
        authService.logout();
        navigate(ROUTES.auth.login);
        setOpenDialog(false);
    };

    const handleAccordionToggle = (index: string, isCurrentlyOpen: boolean) => {
        // Устанавливаем флаг ручного переключения
        setIsManualToggle(true);
        
        // Если аккордеон уже открыт, закрываем его
        if (isCurrentlyOpen) {
            setAccordionOpen(prev => ({
                ...prev,
                [index]: false,
            }));
            // Сбрасываем флаг после времени анимации закрытия
            setTimeout(() => {
                setIsManualToggle(false);
            }, ACCORDION_ANIMATION_DURATION.exit);
        } else {
            // Находим индекс открытого аккордеона
            const currentlyOpenIndex = Object.keys(accordionOpen).find(key => accordionOpen[key] === true);
            
            if (currentlyOpenIndex && currentlyOpenIndex !== index) {
                // Если есть открытый аккордеон и это не тот же самый, закрываем его и открываем новый одновременно
                setClosingIndex(currentlyOpenIndex); // Отслеживаем закрывающийся аккордеон
                
                // Одновременно закрываем старый и открываем новый аккордеон
                setAccordionOpen({
                    [currentlyOpenIndex]: false,
                    [index]: true,
                });
                
                // Сбрасываем отслеживание закрывающегося аккордеона после анимации закрытия
                setTimeout(() => {
                    setClosingIndex(null);
                }, ACCORDION_ANIMATION_DURATION.exit);
                
                // Сбрасываем флаг после времени анимации открытия (берем большее значение)
                setTimeout(() => {
                    setIsManualToggle(false);
                }, ACCORDION_ANIMATION_DURATION.enter);
            } else {
                // Если нет открытых аккордеонов или кликнули на тот же, просто открываем нужный
                setAccordionOpen({ [index]: true });
                
                // Сбрасываем флаг после времени анимации открытия
                setTimeout(() => {
                    setIsManualToggle(false);
                }, ACCORDION_ANIMATION_DURATION.enter);
            }
        }
    };

    const handleNavigate = (link: string) => {
        navigate(link);
    };

    // При изменении пути автоматически открываем аккордеон для активного элемента
    useEffect(() => {
        // Находим индекс активного primary элемента с детьми
        const findActiveAccordionIndex = (items: MenuItem[], parentIndex = ''): string | null => {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (!item.isHideButton) {
                    const currentIndex = `${parentIndex}${i}`;
                    
                    // Если это primary элемент с детьми и он активен
                    if (item.isPrimary && item.children && item.isActive) {
                        return currentIndex;
                    }
                }
            }
            return null;
        };

        const activeIndex = findActiveAccordionIndex(menuItemsState);
        
        // Не обновляем состояние, если было ручное переключение (чтобы не прерывать анимацию)
        if (isManualToggle) {
            return;
        }
        
        // Обновляем состояние только если нужно изменить аккордеон
        setAccordionOpen(prev => {
            const currentActiveIndex = Object.keys(prev).find(key => prev[key] === true);
            
            // Если активный индекс не изменился, не обновляем состояние
            if (activeIndex === currentActiveIndex) {
                return prev;
            }
            
            // Если есть активный аккордеон, открываем только его
            if (activeIndex) {
                return { [activeIndex]: true };
            } else {
                // Если нет активного аккордеона, закрываем все
                return {};
            }
        });
    }, [location.pathname, menuItemsState]);

    const renderMenuItems = (items: MenuItem[], parentIndex = ''): React.ReactNode[] => {
        return items
            .filter(item => !item.isHideButton)
            .map((item, index) => {
                const currentIndex = `${parentIndex}${index}`;
                
                return (
                    <MenuItemComponent
                        key={currentIndex}
                        item={item}
                        currentIndex={currentIndex}
                        accordionOpen={accordionOpen}
                        closingIndex={closingIndex}
                        onAccordionToggle={handleAccordionToggle}
                        onNavigate={handleNavigate}
                        onLogout={() => setOpenDialog(true)}
                        renderChildren={renderMenuItems}
                    />
                );
            });
    };

    return (
        <Drawer
            PaperProps={{
                sx: {
                    backgroundColor: "#F6F6F6",
                    border: "0",
                    overflow: 'hidden', // Скрываем overflow, чтобы предотвратить появление полос прокрутки
                }
            }}
            variant="permanent"
            anchor="left"
            sx={{
                display: { xs: 'none', sm: 'block' },
                '& .MuiDrawer-paper': { 
                    boxSizing: 'border-box', 
                    width: 250,
                    overflow: 'hidden', // Скрываем overflow на paper
                },
            }}
        >
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box display="flex" pl={5} pt={3} sx={{ flexShrink: 0 }}>
                    <Avatar sx={{ bgcolor: deepPurple[500] }}>
                        <MedicalServicesOutlined />
                    </Avatar>
                    <Typography ml={1} mt={1}>
                        Медосмотры
                    </Typography>
                </Box>
                <List 
                    component="nav" 
                    style={{ padding: 10, flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
                    sx={{
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#c0c0c0',
                            borderRadius: '4px',
                            '&:hover': {
                                background: '#a0a0a0',
                            },
                        },
                        '& .MuiListItem-root': {
                            overflow: 'visible',
                        },
                    }}
                >
                    {renderMenuItems(menuItemsState, "")}
                </List>
                <ConfirmDialog
                    open={openDialog}
                    title="Выход"
                    content={
                        <>
                            Вы действительно хотите выйти?
                        </>
                    }
                    onClose={() => setOpenDialog(false)}
                    onConfirm={handleLogout}
                />
            </div>
        </Drawer>
    );
};
